import Vue from 'vue';
import { mapGetters } from 'vuex';
import ChannelVideos from './ChannelVideos.vue';
import ChannelVideosSection from './ChannelVideosSection.vue';
import FuzzySearch from '@lumiere/shared/services/FuzzySearch';
import intersection from 'lodash/intersection';
const SEARCH_KEYS = ['title', 'description'];
const channelVideosFuse = FuzzySearch([], { keys: SEARCH_KEYS });
const INITIAL_RENDER = 25;
export default Vue.extend({
    name: 'ChannelVideosWrapper',
    data() {
        return {
            observer: null,
            renderedVideosCount: INITIAL_RENDER,
            observers: [],
        };
    },
    props: {
        channel: {
            type: Object,
        },
        videos: {
            type: Array,
        },
    },
    mounted() {
        const observerRef = this.$refs.observerTarget;
        if (observerRef) {
            this.createObserver(observerRef, this.intersectionHandler);
        }
    },
    methods: {
        // callback: IntersectionObserverCallback
        createObserver(el, callback) {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0,
            };
            const observer = new IntersectionObserver(callback, options);
            observer.observe(el);
            this.observers.push({ observer, target: el });
        },
        intersectionHandler(entries, _observer) {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.renderedVideosCount *= 2;
                }
            });
        },
    },
    computed: {
        ...mapGetters(['channelTags']),
        videoFilters() {
            return this.$attrs.getFilters();
        },
        isGroupedByTags() {
            return this.channel.options.groupByTag;
        },
        filteredVideos() {
            const { tag, search } = this.videoFilters;
            const videos = this.videos;
            if (search) {
                channelVideosFuse.setCollection(videos ?? []);
            }
            return tag
                ? videos.filter((video) => video.tags.includes(tag))
                : search
                    ? channelVideosFuse.search(search).map(({ item }) => item)
                    : videos;
        },
        filteredVideosByTags() {
            const channelTags = this.channelTags;
            return channelTags.reduce((acc, cur) => {
                acc[cur.id] = this.filteredVideos.filter((video) => video.tags.includes(cur.id));
                return acc;
            }, {});
        },
        allFilteredVideosByTags() {
            let allVideos = [];
            for (let tagId in this.filteredVideosByTags) {
                allVideos = allVideos.concat(this.filteredVideosByTags[tagId]);
            }
            return allVideos;
        },
        videosByTag() {
            return (tagId) => this.filteredVideos
                .filter((video) => video.tags.includes(tagId))
                .slice(0, this.renderedVideosCount);
        },
        untaggedVideos() {
            if (this.tagFilter.length < this.channelTags.length)
                return [];
            return this.filteredVideos
                .filter((video) => {
                const tagIdKeys = Object.keys(this.filteredVideosByTags);
                const tagOrphaned = intersection(video.tags, tagIdKeys);
                return !video.tags.length || !tagOrphaned.length;
            })
                .slice(0, this.renderedVideosCount);
        },
        tagFilter() {
            const findTag = (id) => {
                return this.channelTags.find((el) => el.id === id);
            };
            const filteredVideosByTags = this.filteredVideosByTags;
            let tagIdAsKeys = Object.keys(filteredVideosByTags);
            let channelTags = [];
            let foundTag;
            let tagId;
            let cum = 0;
            for (let i = 0; i < tagIdAsKeys.length; i++) {
                tagId = tagIdAsKeys[i];
                if (cum <= this.renderedVideosCount) {
                    foundTag = findTag(tagId);
                    foundTag && channelTags.push(foundTag);
                }
                cum += filteredVideosByTags[tagId].length;
            }
            const { tag } = this.videoFilters;
            return tag ? channelTags.filter((t) => t.id === tag) : channelTags;
        },
        emptyTagFilter() {
            const { tag } = this.videoFilters;
            return !!tag && this.isGroupedByTags
                ? !this.videosByTag(this.tagFilter[0]?.id).length
                : !this.filteredVideos.length;
        },
    },
    components: {
        ChannelVideos,
        ChannelVideosSection,
    },
    beforeDestroy() {
        for (let obs of this.observers) {
            if (obs?.observer) {
                obs.observer.unobserve(obs.target);
            }
        }
    },
});
